import {
  BRANDING_ACTIVE_COLORS_BG,
  BRANDING_ACTIVE_COLORS_FONT,
  BRANDING_IDLE_COLORS_BG,
  BRANDING_IDLE_COLORS_FONT,
  FONTS_WITH_TINYMCE_GOOGLE_FONTS
} from '@opengov/stories-shared/constants';
import { IColorOption, IPublicationBrandings } from '@opengov/stories-shared/types';
import ImageIcon from '@mui/icons-material/Image';
import Box from '@mui/material/Box';
import React, { useMemo, useState } from 'react';

import { styles } from './NavBarPreview.styles';

export type IPreviewMenuItem = {
  displayName: string | null;
  children?: IPreviewMenuItem[];
};

export type INavBarPreviewProps = {
  publicationBrandings: IPublicationBrandings | null;
  menuItems: IPreviewMenuItem[];
};

const getColorCode = (currentList: IColorOption[] | undefined, defaultList: IColorOption[] = []) =>
  currentList?.find((c) => c.selected)?.colorCode || defaultList?.find((c) => c.selected)?.colorCode;

export const NavBarPreview = (props: INavBarPreviewProps) => {
  const { publicationBrandings, menuItems } = props;
  const [activeMenuItemIdx, setActiveMenuItemIdx] = useState<number | null>(null);

  const bgColor = useMemo(
    () => getColorCode(publicationBrandings?.idleBackgroundColor, BRANDING_IDLE_COLORS_BG),
    [publicationBrandings?.idleBackgroundColor]
  );

  const fontFamily = useMemo(
    () => FONTS_WITH_TINYMCE_GOOGLE_FONTS.find((f) => f.id === publicationBrandings?.fontStyle)?.name,
    [publicationBrandings?.fontStyle]
  );

  const activeBgColor = useMemo(
    () => getColorCode(publicationBrandings?.activeBackgroundColor, BRANDING_ACTIVE_COLORS_BG),
    [publicationBrandings?.activeBackgroundColor]
  );

  const fontColor = useMemo(
    () => getColorCode(publicationBrandings?.idleFontColor, BRANDING_IDLE_COLORS_FONT),
    [publicationBrandings?.idleFontColor]
  );

  const activeFontColor = useMemo(
    () => getColorCode(publicationBrandings?.activeFontColor, BRANDING_ACTIVE_COLORS_FONT),
    [publicationBrandings?.activeFontColor]
  );

  return (
    <Box sx={styles.container} style={{ background: bgColor }} data-test="nav-bar-preview">
      {!publicationBrandings?.logoUrl ? (
        <Box sx={[styles.logoContainer, styles.logoPlaceholder]} data-test="logo-placeholder">
          <ImageIcon aria-hidden={true} />
        </Box>
      ) : (
        <Box sx={styles.logoContainer}>
          <img alt="logo" src={publicationBrandings.logoUrl} data-test="logo-image" />
        </Box>
      )}
      <Box sx={styles.navMenu} style={{ fontFamily: `"${fontFamily}"` }}>
        {menuItems.map((menuItem, idx) => {
          const isActiveItem = activeMenuItemIdx === idx;
          const color = isActiveItem ? activeFontColor : fontColor;
          return (
            <Box
              key={idx}
              sx={styles.navItem}
              data-test="menu-item"
              style={{ color, backgroundColor: isActiveItem ? activeBgColor : 'transparent' }}
              onMouseEnter={() => setActiveMenuItemIdx(idx)}
              onMouseLeave={() => setActiveMenuItemIdx(null)}
            >
              {menuItem.displayName}
              {!!menuItem.children?.length && (
                <svg
                  width={16}
                  height={16}
                  viewBox="0 0 16 16"
                  aria-label="Down"
                  aria-hidden={false}
                  focusable="false"
                  fill={color}
                >
                  <path d="M16,2.5l-0.1,0.1L8,9L0,2.5V7l6.6,5.3L8,13.5l1.4-1.2L16,7.1V2.5L16,2.5z" />
                </svg>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
